import { RxDocument } from 'rxdb'
import { useShallow } from 'zustand/react/shallow'

import {
  buildClearSnoozePatch,
  extractSnoozeProps,
  getActiveSnoozeCount,
  incrementSnooze,
} from '~/boundedContext/cards/utils/card'
import { Card } from '~/db/types'
import { useCardsGameStore } from '~/stores/cards-game'
import { useSettingsStore } from '~/stores/settings'

export const useCardActions = (activeCard: RxDocument<Card>) => {
  const {
    shuffledCards,
    activeItemIndex,
    decreaseActiveItemIndex,
    increaseActiveItemIndex,
    setNotGuessedCards,
    rememberSnoozePropsBeforePlay,
    takeSnoozePropsBeforePlay,
    notGuessedCards,
  } = useCardsGameStore(
    useShallow(state => ({
      shuffledCards: state.shuffledCards,
      activeItemIndex: state.activeItemIndex,
      decreaseActiveItemIndex: state.decreaseActiveItemIndex,
      increaseActiveItemIndex: state.increaseActiveItemIndex,
      setNotGuessedCards: state.setNotGuessedCards,
      rememberSnoozePropsBeforePlay: state.rememberSnoozePropsBeforePlay,
      takeSnoozePropsBeforePlay: state.takeSnoozePropsBeforePlay,
      notGuessedCards: state.notGuessedCards,
    })),
  )

  const revertLastCard = () => {
    const revertedCard = shuffledCards[activeItemIndex + 1]
    if (!revertedCard) {
      console.error({ message: 'Reverted card not found' })
      return
    }

    increaseActiveItemIndex()

    const isRevertedWastNotGuessed = revertedCard.id === notGuessedCards.at(-1)?.id
    if (isRevertedWastNotGuessed) setNotGuessedCards(notGuessedCards.slice(0, -1))

    const snoozePropsBeforePlay = takeSnoozePropsBeforePlay(revertedCard.id)
    if (snoozePropsBeforePlay) return revertedCard.incrementalPatch(snoozePropsBeforePlay)
  }

  const snoozeCard = async () => {
    rememberSnoozePropsBeforePlay(activeCard.id, extractSnoozeProps(activeCard))

    await activeCard.incrementalPatch(incrementSnooze(activeCard))
  }

  const cardUnknown = async () => {
    setNotGuessedCards([...notGuessedCards, shuffledCards[activeItemIndex] as RxDocument<Card>])

    const { cardsFront, scopeSnoozeToFrontLanguage } = useSettingsStore.getState()
    const hasSnoozeToClear = scopeSnoozeToFrontLanguage
      ? !!getActiveSnoozeCount(activeCard, cardsFront)
      : !!(activeCard.snoozeCountSource || activeCard.snoozeCountLearn)
    if (!hasSnoozeToClear) return

    rememberSnoozePropsBeforePlay(activeCard.id, extractSnoozeProps(activeCard))
    await activeCard.incrementalPatch(buildClearSnoozePatch())
  }

  return {
    cardUnknown,
    cardPlayed: decreaseActiveItemIndex,
    revertLastCard,
    snoozeCard,
  }
}
